/**
 * Politics Jeans collection page.
 *
 * - Size / Color / Sort popovers apply instantly.
 * - "All filters" drawer applies on "Show results".
 * - Results re-render through the Section Rendering API with native
 *   `filter.*` / `sort_by` URL params, and the URL is pushed to history.
 * - "See more products" appends the next `?page=` in place. No infinite scroll.
 *
 * Any fetch failure falls back to a normal page load of the same URL.
 */
class PjCollection extends HTMLElement {
  #abort = new AbortController();
  /** @type {AbortController | null} */
  #request = null;

  get sectionId() {
    return this.dataset.sectionId ?? '';
  }

  connectedCallback() {
    const { signal } = this.#abort;

    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('submit', this.#onSubmit, { signal });
    this.addEventListener('toggle', this.#onToggle, { signal, capture: true });

    document.addEventListener(
      'click',
      (e) => {
        if (!(e.target instanceof Node)) return;
        for (const popover of this.querySelectorAll('details[data-pj-popover][open]')) {
          if (!popover.contains(e.target)) popover.removeAttribute('open');
        }
      },
      { signal }
    );

    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key !== 'Escape') return;
        const open = /** @type {HTMLDetailsElement | null} */ (this.querySelector('details[data-pj-popover][open]'));
        if (!open) return;
        open.removeAttribute('open');
        /** @type {HTMLElement | null} */ (open.querySelector('summary'))?.focus();
      },
      { signal }
    );

    window.addEventListener('popstate', () => window.location.reload(), { signal });
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#request?.abort();
  }

  /** Only one popover open at a time. @param {Event} event */
  #onToggle = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLDetailsElement) || !target.matches('[data-pj-popover]') || !target.open) return;
    for (const other of this.querySelectorAll('details[data-pj-popover][open]')) {
      if (other !== target) other.removeAttribute('open');
    }
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    // Mixed-case size values ("s"/"S") share one visible checkbox; keep hidden twins in sync.
    if (input.dataset.pjMirror !== undefined) {
      for (const twin of this.querySelectorAll('[data-pj-mirror-of]')) {
        const el = /** @type {HTMLInputElement} */ (twin);
        if (el.dataset.pjMirrorOf === input.value && el.name === input.name) el.checked = input.checked;
      }
    }

    // Keep the bar sort and the drawer sort in step.
    if (input.name === 'sort_by') {
      for (const radio of this.querySelectorAll('input[name="sort_by"]')) {
        const el = /** @type {HTMLInputElement} */ (radio);
        el.checked = el.value === input.value;
      }
    }

    if (input.hasAttribute('data-pj-filter')) {
      this.#navigate(this.#buildUrl());
    }
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const drawer = /** @type {HTMLDialogElement | null} */ (this.querySelector('#PjFilterDrawer'));

    if (target.closest('[data-pj-filters-open]')) {
      for (const popover of this.querySelectorAll('details[data-pj-popover][open]')) popover.removeAttribute('open');
      drawer?.showModal();
      return;
    }

    if (target.closest('[data-pj-filters-close]') || target === drawer) {
      drawer?.close();
      return;
    }

    const more = /** @type {HTMLAnchorElement | null} */ (target.closest('[data-pj-see-more]'));
    if (more) {
      event.preventDefault();
      this.#loadMore(more);
      return;
    }

    // Size chip: one chip covers every spelling of a size ("s" + "S"), so uncheck them all.
    const sizeChip = /** @type {HTMLElement | null} */ (target.closest('[data-pj-size-chip]'));
    if (sizeChip) {
      event.preventDefault();
      const label = sizeChip.dataset.pjSizeChip;
      for (const option of this.querySelectorAll('.pj-size-option')) {
        if (option.textContent?.trim().toUpperCase() !== label) continue;
        const input = /** @type {HTMLInputElement | null} */ (option.querySelector('input'));
        if (!input) continue;
        input.checked = false;
        for (const twin of this.querySelectorAll('[data-pj-mirror-of]')) {
          const el = /** @type {HTMLInputElement} */ (twin);
          if (el.dataset.pjMirrorOf === input.value && el.name === input.name) el.checked = false;
        }
      }
      this.#navigate(this.#buildUrl());
      return;
    }

    // Active-filter chips and "Clear all" links: render in place.
    const link = /** @type {HTMLAnchorElement | null} */ (target.closest('.pj-active-filters a, .pj-empty a'));
    if (link && link.href) {
      event.preventDefault();
      this.#navigate(link.href);
    }
  };

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.id !== 'PjFilterForm') return;
    event.preventDefault();
    /** @type {HTMLDialogElement | null} */ (this.querySelector('#PjFilterDrawer'))?.close();
    this.#navigate(this.#buildUrl());
  };

  #buildUrl() {
    const url = new URL(this.dataset.url ?? window.location.pathname, window.location.origin);
    const params = new URLSearchParams();

    for (const node of this.querySelectorAll('input[form="PjFilterForm"]')) {
      const input = /** @type {HTMLInputElement} */ (node);
      if (!input.name) continue;
      if ((input.type === 'checkbox' || input.type === 'radio') && !input.checked) continue;
      if (input.type === 'number' && input.value.trim() === '') continue;
      params.append(input.name, input.value);
    }

    url.search = params.toString();
    return url.toString();
  }

  /**
   * Fetches this section for `url`.
   * @param {string} url
   * @returns {Promise<HTMLElement | null>} the new <pj-collection>
   */
  async #fetchSection(url) {
    this.#request?.abort();
    this.#request = new AbortController();
    const target = new URL(url, window.location.origin);
    target.searchParams.set('section_id', this.sectionId);
    // Pass a string: some app scripts (e.g. shipping protection) wrap fetch and assume a string URL.
    const response = await fetch(target.toString(), { signal: this.#request.signal });
    if (!response.ok) throw new Error(`Section render failed: ${response.status}`);
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector('pj-collection');
  }

  /** @param {string} url */
  async #navigate(url) {
    this.setAttribute('aria-busy', 'true');
    try {
      const fresh = await this.#fetchSection(url);
      if (!fresh) throw new Error('Missing pj-collection in response');

      /** @type {HTMLDialogElement | null} */ (this.querySelector('#PjFilterDrawer'))?.close();
      this.replaceChildren(...Array.from(fresh.childNodes).map((node) => document.importNode(node, true)));
      history.pushState({ pjCollection: true }, '', url);

      const bar = this.querySelector('[data-pj-filterbar]');
      if (bar && bar.getBoundingClientRect().top < 0) {
        const top = window.scrollY + this.getBoundingClientRect().top + (this.querySelector('.pj-collection__head')?.clientHeight ?? 0);
        window.scrollTo({ top, behavior: 'instant' });
      }
      PjCollection.#refreshApps();
    } catch (error) {
      if (/** @type {Error} */ (error).name === 'AbortError') return;
      window.location.assign(url);
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  /** @param {HTMLAnchorElement} button */
  async #loadMore(button) {
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    const label = button.textContent;
    button.textContent = 'Loading…';

    try {
      const fresh = await this.#fetchSection(button.href);
      const grid = this.querySelector('[data-pj-grid]');
      const newCells = fresh?.querySelectorAll('[data-pj-cell]');
      const newPagination = fresh?.querySelector('[data-pj-pagination]');
      if (!grid || !newCells || !newPagination) throw new Error('Unexpected response');

      const firstNew = /** @type {HTMLElement | undefined} */ (newCells[0]);
      for (const cell of newCells) grid.append(document.importNode(cell, true));
      this.querySelector('[data-pj-pagination]')?.replaceWith(document.importNode(newPagination, true));

      // Move focus to the first new product for keyboard and screen reader users.
      if (firstNew) {
        const id = firstNew.querySelector('[data-product-id]')?.getAttribute('data-product-id');
        const link = /** @type {HTMLElement | null} */ (
          grid.querySelector(`[data-product-id="${id}"] .pj-card__link`)
        );
        link?.focus({ preventScroll: true });
      }
      PjCollection.#refreshApps();
    } catch (error) {
      if (/** @type {Error} */ (error).name === 'AbortError') return;
      window.location.assign(button.href);
    } finally {
      button.removeAttribute('aria-busy');
      if (button.isConnected) button.textContent = label;
    }
  }

  /** Let app widgets (Okendo stars) pick up newly inserted cards. */
  static #refreshApps() {
    const okendo = /** @type {any} */ (window).okeWidgetApi;
    try {
      okendo?.initAllWidgets?.();
    } catch {
      /* Okendo not ready; its server-rendered snippet is already in the markup */
    }
  }
}

if (!customElements.get('pj-collection')) customElements.define('pj-collection', PjCollection);
